import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, PartyPopper } from "lucide-react";
import PageHeader from "../components/PageHeader";
import api from "../lib/api";

export default function Trainings() {
    const [trainings, setTrainings] = useState([]);

    const load = useCallback(() => {
        api.get("/trainings").then((r) => setTrainings(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const fmt = (d) =>
        new Date(d + "T00:00:00").toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
            year: "numeric",
        });

    return (
        <div data-testid="trainings-page" className="pb-28">
            <PageHeader
                testid="trainings-header"
                breadcrumb="CE Hours & Trainings"
                title="For therapists who play"
                number="CE"
                label="Continuing Education"
                heading="Learn it. Play it. Teach it."
            >
                <div className="mt-5 flex flex-wrap gap-3">
                    <span className="rounded-full border border-amber-500/25 bg-[#1F1510]/60 px-4 py-1.5 text-xs text-amber-200/80">
                        APT Approved Provider #22-666
                    </span>
                    <span className="rounded-full border border-amber-500/25 bg-[#1F1510]/60 px-4 py-1.5 text-xs text-amber-200/80">
                        EMDRIA Approved Provider #23002
                    </span>
                </div>

                <p className="mt-6 max-w-2xl text-base leading-relaxed text-amber-100/70">
                    Dora Henderson — M.A., LMHC-QS, RPT-S, Certified Safe & Sound
                    Practitioner — teaches the way she practices: hands-on,
                    neuroscience-backed, and genuinely fun. Only upcoming dates
                    are shown below.
                </p>

                {trainings.length === 0 ? (
                    <p
                        data-testid="trainings-empty"
                        className="mt-10 rounded-2xl border border-dashed border-amber-500/25 bg-[#1F1510]/50 p-8 text-center font-serif text-lg italic text-amber-200/70"
                    >
                        No upcoming trainings on the calendar right now — check
                        back soon.
                    </p>
                ) : (
                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {trainings.map((t, i) => (
                            <motion.a
                                key={t.id}
                                data-testid="training-card"
                                href={t.href || "#"}
                                target={t.href ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className="group rounded-2xl border border-amber-500/15 bg-[#1F1510]/50 p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-amber-400/50"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-xs uppercase tracking-[0.2em] text-amber-400/80">
                                        {t.tag}
                                    </span>
                                    <span className="text-xs text-amber-200/70">
                                        {fmt(t.date)}
                                    </span>
                                </div>
                                <h3 className="mt-3 font-serif text-xl text-amber-100">
                                    {t.title}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-amber-100/60">
                                    {t.body}
                                </p>
                                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300">
                                    Details & registration
                                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </span>
                            </motion.a>
                        ))}
                    </div>
                )}

                <div className="mt-10 flex flex-col items-start gap-4 rounded-3xl border border-amber-500/20 bg-[#281D17] p-8 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-amber-300">
                            <PartyPopper className="h-5 w-5" />
                            <span className="font-serif text-xl text-amber-100">
                                Pop In & Play
                            </span>
                        </div>
                        <p className="mt-2 max-w-xl text-sm leading-relaxed text-amber-100/65">
                            Our casual community play day — come see the room,
                            meet the team, and let the kids explore. Free and open
                            to families.
                        </p>
                    </div>
                    <a
                        href="https://a-safe-space.ce-go.com/pop-in-and-play"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex shrink-0 items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-[#1F1510] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-amber-400"
                    >
                        See the next date
                        <ArrowUpRight className="h-4 w-4" />
                    </a>
                </div>
            </PageHeader>
        </div>
    );
}
